import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import { EditIcon, LogOutIcon, MenuIcon, Moon, Sun } from 'lucide-react';
import Toolbar from '@mui/material/Toolbar';
import { menuItems } from '../menuItems';
import { useTheme } from '@mui/material/styles';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import {
  Avatar,
  AvatarImage,
  ControllersContainer,
  DrawerStack,
  Logo,
  NavDrawer,
  NavTypography,
  SubmenuList,
  SidebarMenuItem,
  TopBar,
  UserContainer,
} from '../styles';
import Typography from '@mui/material/Typography';
import { useAuth } from '@/hooks/useAuth';
import { useThemeMode } from '@/context/ThemeContext';
import Skeleton from '@mui/material/Skeleton';
import { useUser } from '@/context/UserContext';
import { getInitials, stringToColor } from '@/utils/stringUtils';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import { Link } from 'react-router-dom';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTranslation } from 'react-i18next';
interface MobileProps {
  expanded: boolean;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  onEditUser: () => void;
}

const drawerWidth = '80vw';

function Mobile({ expanded, setExpanded, onEditUser }: MobileProps) {
  const theme = useTheme();
  const currentPath = location.pathname;
  const navRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useUser();
  const { t } = useTranslation();
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const { logout } = useAuth();
  const { toggleTheme, mode } = useThemeMode();
  const imageUrlPath = user?.image?.url
    ? new URL(user.image.url).pathname.replace(/^\/+/, '')
    : '';
  const name = user?.name || '';
  const initials = useMemo(() => getInitials(name || ''), [name]);
  const bgColor = useMemo(
    () => stringToColor(name || Math.random().toString()),
    [name]
  );

  const handleDrawerClose = () => {
    setIsClosing(true);
    setExpanded(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setExpanded(!expanded);
    }
  };

  useEffect(() => {
    console.log('render');
    setLoading(false);
    function handleClickOutside(event: MouseEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setExpanded(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setExpanded]);

  const handleLogout = () => {
    logout();
  };

  const handleClick = () => {
    setOpen((prev) => !prev);
  };

  const toggleEditUser = () => {
    onEditUser();
    handleDrawerClose();
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <TopBar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={() => {
              handleDrawerToggle();
              setExpanded(true);
            }}
            sx={{ mr: 2 }}
          >
            <MenuIcon color={theme.palette.primary['01']} />
          </IconButton>
          <Logo />
        </TopBar>
      </AppBar>
      <Box
        component="nav"
        sx={{ flexShrink: 0 }}
        aria-label="navigation drawers"
      >
        <NavDrawer
          slotProps={{
            root: {
              keepMounted: true, // Better open performance on mobile.
            },
          }}
          variant="temporary"
          anchor="left"
          open={expanded}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
        >
          <Toolbar />
          <Divider />
          <DrawerStack>
            <Stack>
              <List>
                {menuItems(t, user?.role).map(
                  ({ path, icon, label, submenu }) => (
                    <>
                      <SidebarMenuItem
                        data-active={currentPath === path ? true : false}
                        data-expanded={expanded}
                        to={submenu ? undefined : path}
                        sx={{ width: '100%', borderRadius: 0 }}
                        onClick={submenu ? handleClick : undefined}
                      >
                        {icon}
                        <NavTypography variant="caption">{label}</NavTypography>
                      </SidebarMenuItem>

                      {submenu && expanded && (
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <SubmenuList>
                            {submenu.map((option) => (
                              <Link
                                to={option.path}
                                key={option.path}
                                onClick={handleDrawerClose}
                              >
                                <ListItemButton key={option.path}>
                                  <ListItemIcon>{option.icon}</ListItemIcon>
                                  <ListItemText>{option.label}</ListItemText>
                                </ListItemButton>
                              </Link>
                            ))}
                          </SubmenuList>
                        </Collapse>
                      )}
                    </>
                  )
                )}
              </List>
            </Stack>

            <div style={{ flex: 1 }} />

            <Stack mt="48px" alignItems="flex-start">
              <ControllersContainer
                expanded={expanded}
                sx={{ justifyContent: 'space-around' }}
              >
                <LogOutIcon onClick={handleLogout} cursor="pointer" />

                {mode === 'dark' ? (
                  <Sun onClick={toggleTheme} cursor="pointer" />
                ) : (
                  <Moon onClick={toggleTheme} cursor="pointer" />
                )}
              </ControllersContainer>

              <UserContainer onClick={toggleEditUser} sx={{ width: '100%' }}>
                {loading ? (
                  <Skeleton variant="circular" height={48} width={48} />
                ) : (
                  <Stack alignItems="center">
                    <Avatar
                      $bgColor={user?.image?.url ? 'transparent' : bgColor}
                      $size={48}
                    >
                      {user?.image?.url ? (
                        <AvatarImage
                          src={'/image/' + imageUrlPath} // usa a URL exata do backend
                          alt={user.name || undefined}
                        />
                      ) : initials && initials.trim() ? (
                        initials
                      ) : (
                        '?'
                      )}
                    </Avatar>
                  </Stack>
                )}
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={2}
                  justifyContent="space-between"
                  width="100%"
                >
                  <div style={{ color: 'white', textAlign: 'left' }}>
                    <Typography variant="body1">{name}</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {user?.role === 'OPERATOR'
                        ? t('config.users.OPERATOR')
                        : t('config.users.ADMIN')}{' '}
                    </Typography>
                  </div>
                  <div style={{ flex: 1 }} />
                  <div style={{ lineHeight: 0, justifySelf: 'flex-end' }}>
                    <EditIcon color="white" />
                  </div>
                </Stack>
              </UserContainer>
            </Stack>
          </DrawerStack>
        </NavDrawer>
      </Box>
    </>
  );
}

export default Mobile;
